<script>
  import { createEventDispatcher, onMount } from "svelte"
  import Modal from "../Modal.svelte"
  import Spinner from "../Spinner.svelte"
  import { BASE_URL } from "../../stores/BASE_URL"
  import { user } from "../../stores/User"
  import getMoneda from "../../services/moneda.js"

  export let clienteResumen

  const dispatch = createEventDispatcher()
  let isOpen = true
  let cliente = null
  let comentarios = []
  let loading = true
  let error = ""
  let errorComentarios = ""
  let controller

  const mostrar = (valor) =>
    valor === null || valor === undefined || String(valor).trim() === ""
      ? "Sin registrar"
      : valor
  const moneda = (valor) =>
    valor === null || valor === undefined || valor === ""
      ? "Sin registrar"
      : getMoneda(valor)

  const consultar = async (ops, signal) => {
    const params = new URLSearchParams({
      ops,
      id: clienteResumen.id,
      session_key: $user.session_key,
    })
    const response = await fetch(`${BASE_URL}crm.php?${params}`, { signal })
    if (!response.ok) throw new Error("No se pudo completar la consulta.")
    const respuesta = await response.json()
    if (
      respuesta.status !== "ok" &&
      !(
        ops === "getComentarios" &&
        respuesta.message === "No se encontraron resultados"
      )
    ) {
      throw new Error(
        respuesta.message || "No se pudo obtener la información registrada.",
      )
    }
    return respuesta.data
  }

  const cargar = async () => {
    controller?.abort()
    const actual = new AbortController()
    controller = actual
    loading = true
    error = ""
    errorComentarios = ""
    cliente = null
    comentarios = []

    const [ficha, historial] = await Promise.allSettled([
      consultar("getCliente", actual.signal),
      consultar("getComentarios", actual.signal),
    ])
    if (actual.signal.aborted) return

    if (
      ficha.status === "fulfilled" &&
      ficha.value &&
      typeof ficha.value === "object" &&
      !Array.isArray(ficha.value)
    ) {
      cliente = ficha.value
    } else {
      error =
        ficha.status === "rejected"
          ? ficha.reason.message
          : "No se encontró la ficha del cliente."
    }
    if (historial.status === "fulfilled" && Array.isArray(historial.value)) {
      comentarios = historial.value
    } else {
      errorComentarios = "No se pudieron cargar los comentarios registrados."
    }
    loading = false
  }

  onMount(() => {
    cargar()
    return () => controller?.abort()
  })

  const tieneDato = (valor) =>
    valor !== null && valor !== undefined && String(valor).trim() !== ""
  const unir = (valores) =>
    valores
      .filter(tieneDato)
      .map((valor) => String(valor).trim())
      .join(", ") || "Sin registrar"
  const coloresEtapa = {
    "posible cliente": "secondary",
    "en proceso": "yellow",
    cierre: "success",
    perdido: "danger",
    postventa: "neutral",
    "post venta": "neutral",
  }

  $: colorEtapa =
    coloresEtapa[cliente?.etapa?.trim().toLowerCase()] || "secondary"
  $: iniciales =
    (cliente?.nombres || "")
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((parte) => parte[0] || "")
      .join("")
      .toUpperCase() || "—"
  $: probabilidad =
    tieneDato(cliente?.probabilidad) &&
    Number.isFinite(Number(cliente.probabilidad))
      ? Math.min(100, Math.max(0, Number(cliente.probabilidad)))
      : null
  $: telefonos = unir([cliente?.telef_principal, cliente?.telef_secundario])
  $: correos = unir([cliente?.correo_principal, cliente?.correo_secundario])
  $: direccion = unir([
    cliente?.direccion,
    cliente?.departamento,
    cliente?.provincia,
    cliente?.distrito,
    /^0+$/.test(String(cliente?.ubigeo ?? "").trim()) ? null : cliente?.ubigeo,
  ])
  $: datosResumen = cliente
    ? [
        ["Usuario", cliente.vendedor || clienteResumen.vendedor],
        ["Fecha de creación", cliente.fecha_creacion],
        ["Último contacto", cliente.fecha_actualizacion],
        ["Segmento", cliente.segmento || clienteResumen.segmento],
        ["Producto", cliente.producto],
        [
          "Periodo",
          cliente.year && cliente.month
            ? `${cliente.year}-${String(cliente.month).padStart(2, "0")}`
            : null,
        ],
      ]
    : []
  $: datosLaborales = cliente
    ? [
        ["Razón social", cliente.laboral_razon_social],
        ["RUC", cliente.laboral_ruc],
        ["Devengue", cliente.laboral_devengue],
        [
          "Ingreso",
          tieneDato(cliente.laboral_p1) ? moneda(cliente.laboral_p1) : null,
        ],
      ].filter(([, valor]) => tieneDato(valor))
    : []
</script>

<Modal
  bind:isOpen
  title="Información registrada del cliente"
  size="fullscreen"
  on:close={() => dispatch("close")}
>
  {#if loading}
    <Spinner />
  {:else if error}
    <div class="alert theme-danger" role="alert">
      {error}
      <button
        type="button"
        class="btn btn-sm btn-outline theme-danger ms-3"
        on:click={cargar}>Reintentar</button
      >
    </div>
  {:else}
    <div class="resumen">
      <div
        class="resumen-cabecera d-flex flex-column lg:flex-row justify-content-between align-items-start gap-5 p-7 rounded mb-5"
      >
        <div class="d-flex align-items-center gap-5 identidad">
          <span
            class="avatar avatar-lg theme-primary flex-shrink-0"
            aria-hidden="true">{iniciales}</span
          >
          <div class="valor">
            <p class="fg-primary fw-semibold small mb-1">REGISTRO CRM</p>
            <h2 class="fs-xl fw-semibold mb-3">{mostrar(cliente.nombres)}</h2>
            <span class="badge badge-outline theme-secondary"
              >{cliente.tipo_doc || "Documento"}: {mostrar(
                cliente.documento,
              )}</span
            >
          </div>
        </div>
        <div class="flex-shrink-0 text-start lg:text-center">
          <p class="fg-secondary small mb-3">Etapa comercial</p>
          <span class="badge theme-{colorEtapa} fs-sm px-5 py-3"
            >{mostrar(cliente.etapa)}</span
          >
          <p class="fg-secondary small mt-3 mb-0">
            {mostrar(cliente.clasificacion)}
          </p>
        </div>
      </div>

      <dl class="row g-5 mb-5 pb-5 border-bottom">
        {#each datosResumen.slice(0, 3) as [nombre, valor]}
          <div class="col-12 sm:col-6 lg:col-4">
            <dt class="fg-secondary fw-normal small">{nombre}</dt>
            <dd class="valor fw-medium mt-1 mb-0">{mostrar(valor)}</dd>
          </div>
        {/each}
      </dl>

      <section>
        <h3 class="fs-md fw-semibold d-flex align-items-center gap-3 mb-5">
          <i class="bi bi-briefcase fg-primary" aria-hidden="true"></i> Resumen comercial
        </h3>
        <dl class="row g-5 mb-5">
          {#each datosResumen.slice(3) as [nombre, valor]}
            <div class="col-12 sm:col-6 lg:col-4">
              <dt class="fg-secondary fw-normal small">{nombre}</dt>
              <dd class="valor fw-medium mt-1 mb-0">{mostrar(valor)}</dd>
            </div>
          {/each}
        </dl>
        <div class="bg-2 rounded p-5">
          <div class="row g-5 align-items-center">
            <div class="col-12 sm:col-6 lg:col-4">
              <p class="fg-secondary small mb-1">Monto factura</p>
              <p class="fs-xl fw-semibold fg-primary mb-0">
                {moneda(cliente.monto)}
              </p>
            </div>
            <div class="col-12 sm:col-6 lg:col-4">
              <p class="fg-secondary small mb-1">Monto adicional</p>
              <p class="fs-xl fw-semibold mb-0">
                {moneda(cliente.monto_adicional)}
              </p>
            </div>
            <div class="col-12 lg:col-4">
              <div
                class="d-flex justify-content-between align-items-center gap-3 mb-3"
              >
                <span class="fg-secondary small">Probabilidad de cierre</span>
                <span class="fw-semibold"
                  >{probabilidad === null
                    ? "Sin registrar"
                    : `${probabilidad}%`}</span
                >
              </div>
              {#if probabilidad !== null}
                <div
                  class="progress resumen-progreso"
                  role="progressbar"
                  aria-label="Probabilidad de cierre"
                  aria-valuenow={probabilidad}
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  <div
                    class="progress-bar theme-primary"
                    style:width={`${probabilidad}%`}
                  ></div>
                </div>
              {/if}
            </div>
          </div>
        </div>
      </section>

      <section
        class="border-top mt-7 pt-5 valor"
        aria-label="Datos verificados"
      >
        <p class="py-3 mb-0">
          <i class="bi bi-telephone me-3 fg-primary" aria-hidden="true"
          ></i><strong class="fw-semibold">Teléfonos verificados:</strong>
          {telefonos}
        </p>
        <p class="py-3 mb-0">
          <i class="bi bi-envelope me-3 fg-primary" aria-hidden="true"
          ></i><strong class="fw-semibold">Correos verificados:</strong>
          {correos}
        </p>
        <p class="py-3 mb-0">
          <i class="bi bi-geo-alt me-3 fg-primary" aria-hidden="true"
          ></i><strong class="fw-semibold">Dirección verificada:</strong>
          {direccion}
        </p>
      </section>

      <section class="border-top mt-5 pt-5">
        <h3 class="fs-md fw-semibold d-flex align-items-center gap-3 mb-5">
          <i class="bi bi-buildings fg-primary" aria-hidden="true"></i> Datos laborales
        </h3>
        {#if datosLaborales.length}
          <dl class="row g-5 mb-0">
            {#each datosLaborales as [nombre, valor]}
              <div class="col-12 sm:col-6 lg:col-3">
                <dt class="fg-secondary fw-normal small">{nombre}</dt>
                <dd class="valor fw-medium mt-1 mb-0">{valor}</dd>
              </div>
            {/each}
          </dl>
        {:else}
          <p class="fg-secondary mb-0">Sin registrar</p>
        {/if}
      </section>

      <section class="border-top mt-5 pt-5">
        <h3 class="fs-md fw-semibold d-flex align-items-center gap-3 mb-5">
          <i class="bi bi-chat-left-text fg-primary" aria-hidden="true"></i>
          Comentarios registrados {#if !errorComentarios}<span
              class="badge badge-subtle theme-secondary"
              >{comentarios.length}</span
            >{/if}
        </h3>
        {#if errorComentarios}
          <p class="fg-danger mb-0" role="alert">{errorComentarios}</p>
        {:else if comentarios.length}
          <div class="table-responsive">
            <table class="table table-striped table-hover table-xs mb-0">
              <thead class="bg-2"
                ><tr
                  ><th scope="col" class="fg-secondary fw-medium py-3 px-3"
                    >Fecha</th
                  ><th scope="col" class="fg-secondary fw-medium py-3 px-3"
                    >Comentario</th
                  ><th scope="col" class="fg-secondary fw-medium py-3 px-3"
                    >Tipo agenda</th
                  ><th scope="col" class="fg-secondary fw-medium py-3 px-3"
                    >Fecha agenda</th
                  ></tr
                ></thead
              >
              <tbody>
                {#each comentarios as comentario}
                  <tr
                    ><td class="py-3 px-3 text-nowrap"
                      >{mostrar(comentario.fecha_registro)}</td
                    ><td class="comentario py-3 px-3"
                      >{mostrar(comentario.comentarios)}</td
                    ><td class="py-3 px-3">{mostrar(comentario.tipo_agenda)}</td
                    ><td class="py-3 px-3 text-nowrap"
                      >{mostrar(comentario.fecha_hora)}</td
                    ></tr
                  >
                {/each}
              </tbody>
            </table>
          </div>
        {:else}
          <p class="fg-secondary mb-0">No hay comentarios registrados.</p>
        {/if}
      </section>
    </div>
  {/if}
</Modal>

<style>
  .resumen {
    max-width: 1200px;
    margin-inline: auto;
  }
  .resumen-cabecera {
    background-color: var(--dw-primary-50);
    border-inline-start: 4px solid var(--dw-primary-500);
  }
  .identidad {
    min-width: 0;
  }
  .resumen-progreso {
    --bs-progress-height: 0.375rem;
    --bs-progress-bg: var(--dw-neutral-200);
  }
  .valor {
    overflow-wrap: anywhere;
  }
  .comentario {
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    min-width: 180px;
  }
</style>
